import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

import { ConnectionErrorScreen } from '@/src/components/ConnectionErrorScreen'
import { SpinnerIcon } from '@/src/assets/icons'

import { isBitrixProjectMode, resolveProjectMode } from '@/src/constants/projectModes'
import { RoomLookupErrorKind } from '@/src/lib/roomLookupError'
import {
  clearMeetingSession,
  requestForcePrejoin,
} from '@/src/hooks/room/useMeetingSession'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'
import { selectProject } from '@/src/store/slices/room'

const FALLBACK_HOME_URL = 'https://proofix.ru'

interface RoomLookupErrorOverlayProps {
  kind: RoomLookupErrorKind | null
}

/**
 * Берёт только http(s)-ссылку на сайт клиента, иначе proofix.ru.
 *
 * `url_log_out` из брендинга может быть пустым или мусором, и тогда уводить
 * человека «домой» небезопасно.
 */
function sanitizeHomeUrl(url: string | null | undefined): string {
  const trimmed = url?.trim() ?? ''

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed
  }

  return FALLBACK_HOME_URL
}

/**
 * Первое строковое значение query Next, если параметр пришёл массивом.
 */
function firstQueryValue(value: string | string[] | undefined): string | undefined {
  if (Array.isArray(value)) {
    return value[0]
  }

  return value
}

/**
 * Блокирующий оверлей на PreJoin, когда комнату не удалось загрузить.
 *
 * Закрыть его нельзя: при сбое сети страница перезагружается, а если встречи
 * нет — кнопка ведёт на сайт клиента. Форма под карточкой остаётся видимой,
 * но недоступной.
 */
export function RoomLookupErrorOverlay({ kind }: RoomLookupErrorOverlayProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const branding = useAppSelector(selectBranding)
  const projectFromStore = useAppSelector(selectProject)

  const projectFromQuery = firstQueryValue(router.query.project)
  const companySlug = firstQueryValue(router.query.companySlug)

  const isBitrix = isBitrixProjectMode(
    resolveProjectMode(projectFromQuery ?? projectFromStore),
  )

  const [homeUrl, setHomeUrl] = useState(sanitizeHomeUrl(branding?.url_log_out))
  const [hasResolvedHomeUrl, setHasResolvedHomeUrl] = useState(false)

  useEffect(() => {
    if (kind !== 'not_found') {
      setHasResolvedHomeUrl(false)

      return
    }

    // MeetData кладёт project в стор только в useEffect. До isReady query
    // тоже пустой — кнопка иначе сразу ведёт на proofix.ru.
    if (!router.isReady) {
      setHasResolvedHomeUrl(false)

      return
    }

    if (!isBitrix) {
      setHomeUrl(sanitizeHomeUrl(branding?.url_log_out))
      setHasResolvedHomeUrl(true)

      return
    }

    if (!companySlug) {
      setHomeUrl(FALLBACK_HOME_URL)
      setHasResolvedHomeUrl(true)

      return
    }

    let cancelled = false

    setHasResolvedHomeUrl(false)

    fetch(`/api/bitrix_client_branding?companySlug=${encodeURIComponent(companySlug)}`)
      .then(async (response) => {
        if (!response.ok) {
          return null
        }

        return (await response.json()) as { data?: { url_log_out?: string } }
      })
      .then((payload) => {
        if (cancelled) {
          return
        }

        setHomeUrl(sanitizeHomeUrl(payload?.data?.url_log_out))
        setHasResolvedHomeUrl(true)
      })
      .catch(() => {
        if (cancelled) {
          return
        }

        setHomeUrl(FALLBACK_HOME_URL)
        setHasResolvedHomeUrl(true)
      })

    return () => {
      cancelled = true
    }
  }, [kind, isBitrix, branding?.url_log_out, companySlug, router.isReady])

  /**
   * Перезагружает страницу на форму входа.
   *
   * Флаг force-prejoin не даёт авто-входу сразу затянуть человека обратно
   * в тот же звонок.
   */
  const reloadToPrejoin = () => {
    clearMeetingSession()
    requestForcePrejoin()

    const url = new URL(window.location.href)

    url.searchParams.delete('inWaitingRoom')
    window.location.assign(url.toString())
  }

  if (!kind) {
    return null
  }

  if (kind === 'failed') {
    return (
      <ConnectionErrorScreen
        overlay
        buttonLabel={t('tryAgain')}
        onButtonClick={reloadToPrejoin}
      />
    )
  }

  const isHomeUrlLoading = !hasResolvedHomeUrl

  return (
    <div
      className="connection-error-page organizer-code-error-overlay"
      data-lk-theme="default"
    >
      <section
        className="connection-error-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="room-not-found-title"
      >
        <h1 id="room-not-found-title" className="connection-error-title">
          {t('roomNotFoundTitle')}
        </h1>

        <p className="connection-error-description">{t('roomNotFoundDescription')}</p>

        <button
          className="connection-error-button"
          type="button"
          disabled={isHomeUrlLoading}
          onClick={() => {
            window.location.href = homeUrl
          }}
        >
          {isHomeUrlLoading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <SpinnerIcon className="lk-spinner" />
              {t('goHome')}
            </span>
          ) : (
            t('goHome')
          )}
        </button>
      </section>
    </div>
  )
}
